import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Factory } from "lucide-react";
import HubCmdLogo from "@/components/HubCmdLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-12">
            <HubCmdLogo size={36} variant="shield" />
            <span className="font-display font-black text-lg tracking-tight"><span>hub</span><span className="text-[#ff4500]">CMD</span></span>
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Access</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">Sign in to your command center.</h1>
          <p className="mt-3 text-sm text-[#1a1a1a]/70">Welcome back, floor leader.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-1 focus:ring-[#1a1a1a]"
                placeholder="you@plant.com" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Password</label>
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-1 focus:ring-[#1a1a1a]"
                placeholder="••••••••" />
            </div>
            <button
              data-testid="login-submit-button"
              disabled={busy}
              className="w-full bg-[#ff4500] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#cc3500] disabled:opacity-60 transition-colors duration-150">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-sm text-[#1a1a1a]/70">
            Need access?{" "}
            <Link to="/" data-testid="login-to-request-access" className="font-bold text-[#1a1a1a] underline underline-offset-4 decoration-[#ff4500]">
              Request an invitation
            </Link>
          </div>
        </div>
      </div>
      <div
        className="hidden lg:block relative bg-[#1a1a1a]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,139,0.65), rgba(0,0,139,0.4)), url('https://images.unsplash.com/photo-1524514587686-e2909d726e9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwbWFjaGluZXJ5JTIwZ2VhcnMlMjBkZXRhaWx8ZW58MHx8fHwxNzgwMDQyMDAwfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}>
        <div className="absolute inset-0 industrial-grid opacity-25" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/70">// Field note</div>
          <div className="font-display font-black text-2xl mt-3 leading-snug max-w-md">
            "Cut our morning admin from two hours to twenty minutes."
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 mt-3">Plant Supervisor · Tier-1 Auto</div>
        </div>
      </div>
    </div>
  );
}
